<template>
  <v-container fluid class="intelligence-find-similar pa-4">
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center mb-4">
          <v-btn text color="#007AFF" @click="goBack">
            <v-icon left>mdi-arrow-left</v-icon>
            Back
          </v-btn>
        </div>
        <h1 class="text-h5 font-weight-bold mb-1 theme-heading">
          Find Similar Profiles
        </h1>
        <p class="text-subtitle-2 grey--text text--darken-1 mb-4">
          Discover profiles with similar backgrounds and capabilities
        </p>
      </v-col>
    </v-row>

    <v-progress-linear v-if="isLoading || isLoadingSimilar" indeterminate color="#007AFF" class="mb-4" />

    <v-card v-if="hasProfile" outlined class="pa-4 mb-4" style="border-left: 3px solid #007AFF">
      <div class="d-flex align-center">
        <div class="flex-grow-1">
          <div class="text-subtitle-1 font-weight-bold theme-heading">
            {{ profile.name }}
          </div>
          <div class="text-caption grey--text text--darken-1">
            {{ profile.currentTitle }} at {{ profile.currentCompany }}
          </div>
        </div>
        <v-chip small color="#007AFF" text-color="white" class="mr-2" v-if="profile.seniorityLevel">
          {{ profile.seniorityLevel }}
        </v-chip>
        <v-chip small color="#8B5CF6" text-color="white" v-if="profile.specialistCluster">
          {{ profile.specialistCluster }}
        </v-chip>
      </div>
    </v-card>

    <v-card outlined class="pa-4 mb-4">
      <div class="d-flex align-center">
        <span class="text-caption grey--text text--darken-1 mr-3">Min similarity:</span>
        <v-slider
          v-model="minSimilarity"
          :min="0"
          :max="100"
          :step="5"
          color="#007AFF"
          track-color="#E0E0E0"
          thumb-label
          hide-details
          style="max-width: 300px"
        />
        <span class="text-body-2 font-weight-bold ml-3" style="color: #007AFF">
          {{ minSimilarity }}%
        </span>
      </div>
    </v-card>

    <div class="d-flex align-center mb-3" v-if="filteredSimilar.length > 0">
      <div class="text-caption grey--text text--darken-1 flex-grow-1">
        {{ filteredSimilar.length }} similar profiles found
      </div>
      <v-btn x-small text :color="showScores ? '#007AFF' : '#9E9E9E'" @click="toggleScores">
        <v-icon x-small left>{{ showScores ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
        {{ showScores ? 'Scores On' : 'Scores Off' }}
      </v-btn>
    </div>

    <v-row v-if="filteredSimilar.length > 0">
      <v-col cols="12" md="6" v-for="sp in filteredSimilar" :key="sp.profileId">
        <v-card outlined class="similar-card pa-4" @click="viewProfile(sp.profileId)">
          <div class="d-flex align-center mb-3">
            <div class="flex-grow-1">
              <div class="text-subtitle-1 font-weight-bold theme-heading">
                {{ sp.name }}
              </div>
              <div class="text-caption grey--text text--darken-1">
                {{ sp.currentTitle }} at {{ sp.currentCompany }}
              </div>
            </div>
            <v-chip v-if="showScores" small :color="similarityColor(sp.similarityScore)" text-color="white" class="font-weight-bold">
              {{ sp.similarityScore }}%
            </v-chip>
          </div>

          <template v-if="showScores">
            <div class="text-caption grey--text mb-2">Similarity Breakdown</div>
            <div v-for="facet in facetBreakdown(sp)" :key="facet.name" class="d-flex align-center mb-1">
              <span class="text-caption" style="min-width: 100px">{{ facet.name }}</span>
              <v-progress-linear
                :value="facet.score"
                :color="facetColor(facet.score)"
                height="6"
                rounded
                class="mx-2"
              />
              <span class="text-caption font-weight-bold" style="min-width: 40px">{{ facet.score }}%</span>
              <span class="text-caption grey--text ml-1">({{ facet.weight }}%)</span>
            </div>
          </template>

          <div class="d-flex align-center mt-3" v-if="sp.seniorityLevel || sp.specialistCluster">
            <v-chip x-small outlined color="primary" class="mr-2" v-if="sp.seniorityLevel">
              {{ sp.seniorityLevel }}
            </v-chip>
            <v-chip x-small outlined color="#8B5CF6" v-if="sp.specialistCluster">
              {{ sp.specialistCluster }}
            </v-chip>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <div v-else-if="!isLoadingSimilar && !isLoading" class="text-center py-8 grey--text">
      No similar profiles found
    </div>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";

export default {
  name: "IntelligenceFindSimilar",
  data() {
    return {
      minSimilarity: 0,
    };
  },
  computed: {
    ...mapState("intelligenceProfile", [
      "profile",
      "similarProfiles",
      "isLoading",
      "isLoadingSimilar",
    ]),
    ...mapState("intelligenceSearch", ["showScores"]),
    ...mapGetters("intelligenceProfile", ["hasProfile"]),
    filteredSimilar() {
      if (this.minSimilarity <= 0) return this.similarProfiles;
      return this.similarProfiles.filter((sp) => sp.similarityScore >= this.minSimilarity);
    },
  },
  mounted() {
    const profileId = this.$route.params.profileId;
    if (profileId) {
      this.loadProfile(profileId);
      this.loadSimilar(profileId);
    }
  },
  methods: {
    ...mapActions("intelligenceProfile", ["loadProfile", "loadSimilar"]),
    ...mapMutations("intelligenceSearch", ["toggleScores"]),
    goBack() {
      if (window.history.length > 1) {
        const prev = this.$router.resolve({ name: "IntelligenceFindTalent", query: { tab: "search" } });
        this.$router.push(prev.location).catch(() => {});
      } else {
        this.$router.push({ name: "IntelligenceFindTalent", query: { tab: "search" } }).catch(() => {});
      }
    },
    facetBreakdown(sp) {
      if (sp.matchReasons && sp.matchReasons.length > 0) {
        const facets = sp.matchReasons.map((r) => ({
          name: r.facet || r.Facet || r.name || "Unknown",
          score: Math.round(r.score ?? r.Score ?? r.strength ?? 0),
          weight: r.weight > 1 ? r.weight : Math.round((r.weight ?? r.Weight ?? 0) * 100),
          detail: r.detail || r.Detail || "",
        }));
        const facetNames = new Set(facets.map(f => f.name));
        const defaults = [
          { name: "Skills", weight: 45 },
          { name: "Seniority", weight: 15 },
          { name: "Specialist Area", weight: 15 },
          { name: "Function", weight: 10 },
          { name: "Depth", weight: 15 },
        ];
        for (const d of defaults) {
          if (!facetNames.has(d.name)) {
            facets.push({ name: d.name, score: 0, weight: d.weight, detail: "" });
          }
        }
        return facets;
      }
      if (sp.facetBreakdown) {
        return Object.entries(sp.facetBreakdown).map(([name, data]) => ({
          name,
          score: typeof data === 'number' ? data : (data.score || 0),
          weight: typeof data === 'number' ? 0 : (data.weight || 0),
          detail: "",
        }));
      }
      return [
        { name: "Skills", score: sp.skillsSimilarity || 0, weight: 45, detail: "" },
        { name: "Seniority", score: sp.senioritySimilarity || 0, weight: 15, detail: "" },
        { name: "Specialist Area", score: sp.clusterSimilarity || 0, weight: 15, detail: "" },
        { name: "Function", score: sp.functionalSimilarity || 0, weight: 10, detail: "" },
        { name: "Depth", score: sp.depthSimilarity || 0, weight: 15, detail: "" },
      ];
    },
    similarityColor(score) {
      if (score >= 70) return "#10B981";
      if (score >= 50) return "#F59E0B";
      return "#9E9E9E";
    },
    facetColor(score) {
      if (score >= 70) return "#10B981";
      if (score >= 50) return "#007AFF";
      return "#F59E0B";
    },
    viewProfile(profileId) {
      this.$router.push({ name: "IntelligenceProfile", params: { profileId }, query: { from: 'findsimilar' } });
    },
  },
};
</script>

<style scoped>
.intelligence-find-similar {
  max-width: 1200px;
}
.similar-card {
  cursor: pointer;
  transition: box-shadow 0.2s ease;
}
.similar-card:hover {
  box-shadow: 0 2px 8px rgba(30, 58, 95, 0.15);
}
</style>
